import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Mic, MicOff, VideoOff, Settings, User, Volume2, Play, Square, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import Button from '../ui/Button';
import { useRoomStore } from '../../stores/roomStore';
import { useMediaStore } from '../../stores/mediaStore';
import { useRoom } from '../../contexts/RoomContext';
import { useMediaDevices } from '../../hooks/useMediaDevices';
import { useLocalMediaPreview } from '../../hooks/useLocalMediaPreview';
import { useSpeakerTest } from '../../hooks/useSpeakerTest';
import { useMicrophoneTest } from '../../hooks/useMicrophoneTest';
import { useBackgroundBlur } from '../../hooks/useBackgroundBlur';

interface LobbyProps {
  roomId: string;
  displayName: string;
  isHost: boolean;
  initialPassword?: string;
  createRoom?: boolean;
}

const Lobby = ({ roomId, displayName, isHost, initialPassword, createRoom }: LobbyProps) => {
  const navigate = useNavigate();
  const { joinRoom } = useRoom();
  const { setDisplayName, password: storedPassword } = useRoomStore();
  const { 
    setAudioEnabled, 
    setVideoEnabled, 
    audioDevices, 
    videoDevices, 
    audioOutputDevices,
    audioDeviceId, 
    videoDeviceId, 
    audioOutputDeviceId,
    setAudioDeviceId, 
    setVideoDeviceId,
    setAudioOutputDeviceId,
    blurBackground,
    setBlurBackground
  } = useMediaStore();
  const { loading: devicesLoading } = useMediaDevices();
  const { previewStream, loading: previewLoading, error: previewError } = useLocalMediaPreview();
  const { isPlaying: isSpeakerTesting, playTestSound } = useSpeakerTest(audioOutputDeviceId);
  const { isTesting: isMicTesting, volume: micVolume, startTest: toggleMicTest } = useMicrophoneTest(audioDeviceId);
  
  // Background blur
  const { processedStream, canvasRef, isProcessing: blurProcessing } = useBackgroundBlur(
    previewStream,
    { enabled: blurBackground, blurAmount: 15 }
  );
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const [audioEnabled, setLocalAudioEnabled] = useState(true);
  const [videoEnabled, setLocalVideoEnabled] = useState(true);
  const [name, setName] = useState(displayName);
  const [joining, setJoining] = useState(false);
  const [showNameModal, setShowNameModal] = useState(!displayName);
  const [modalName, setModalName] = useState('');
  // Используем сохранённый пароль если он есть (при возврате из комнаты в лобби)
  const effectivePassword = initialPassword || storedPassword || '';
  const [roomPassword, setRoomPassword] = useState(effectivePassword);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingJoinParams, setPendingJoinParams] = useState<{ overrideAudio?: boolean; overrideVideo?: boolean } | null>(null);
  const [roomRequiresPassword, setRoomRequiresPassword] = useState(false);

  // Проверяем требуется ли пароль при входе в лобби (только если пароль не передан и не сохранён)
  useEffect(() => {
    if (initialPassword || storedPassword || createRoom) {
      // Если пароль уже передан, сохранён или создаём новую комнату - не проверяем
      return;
    }

    const checkPassword = async () => {
      try {
        const socket = io(import.meta.env.VITE_WS_URL || window.location.origin, {
          transports: ['websocket'],
          autoConnect: true,
        });

        socket.on('connect', () => {
          socket.emit('checkRoomPassword', { roomId }, (response: any) => {
            console.log('[Lobby] Password check response:', response);
            if (response.success) {
              if (!response.exists) {
                // Комната не существует - перенаправляем на главную
                toast.error('Комната не существует');
                navigate('/');
              } else if (response.needsPassword) {
                // Комната требует пароль - показываем модальное окно
                setRoomRequiresPassword(true);
                setShowPasswordModal(true);
              }
            }
            socket.disconnect();
          });
        });

        socket.on('connect_error', () => {
          console.error('[Lobby] Failed to connect for password check');
        });
      } catch (error) {
        console.error('[Lobby] Error checking password:', error);
      }
    };

    checkPassword();
  }, [roomId, initialPassword, storedPassword, createRoom, navigate]);

  // Подключаем превью стрим к video элементу (используем обработанный стрим если blur включен)
  useEffect(() => {
    if (videoRef.current) {
      if (blurBackground && processedStream) {
        videoRef.current.srcObject = processedStream;
      } else if (previewStream) {
        videoRef.current.srcObject = previewStream;
      }
    }
  }, [previewStream, processedStream, blurBackground]);

  // Управление треками при переключении
  useEffect(() => {
    if (previewStream) {
      const videoTrack = previewStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = videoEnabled;
      }
    }
  }, [previewStream, videoEnabled]);

  useEffect(() => {
    if (previewStream) {
      const audioTrack = previewStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = audioEnabled;
      }
    }
  }, [previewStream, audioEnabled]);

  // Автоперезапуск теста микрофона при смене устройства
  useEffect(() => {
    if (isMicTesting) {
      // Перезапускаем тест при смене устройства
      toggleMicTest(); // Останавливаем
      setTimeout(() => {
        toggleMicTest(); // Запускаем снова
      }, 100);
    }
  }, [audioDeviceId]);

  // Автоперезапуск теста динамика при смене устройства
  useEffect(() => {
    if (isSpeakerTesting) {
      // Перезапускаем тест при смене устройства
      playTestSound(); // Останавливаем
      setTimeout(() => {
        playTestSound(); // Запускаем снова
      }, 100);
    }
  }, [audioOutputDeviceId]);

  const handleJoin = async (overrideAudio?: boolean, overrideVideo?: boolean) => {
    if (!name.trim()) {
      toast.error('Введите ваше имя');
      return;
    }

    setJoining(true);

    try {
      // Используем переопределенные значения если они переданы
      const finalAudioEnabled = overrideAudio !== undefined ? overrideAudio : audioEnabled;
      const finalVideoEnabled = overrideVideo !== undefined ? overrideVideo : videoEnabled;
      
      // Сохраняем настройки
      setDisplayName(name.trim());
      setAudioEnabled(finalAudioEnabled);
      setVideoEnabled(finalVideoEnabled);

      // Подключаемся к комнате с паролем (если есть)
      await joinRoom(roomId, name.trim(), roomPassword || undefined, finalAudioEnabled, finalVideoEnabled, createRoom);
      
      toast.success('Подключено к конференции');
    } catch (error: any) {
      console.error('Failed to join:', error);
      
      // Проверяем, не найдена ли комната
      if (error.roomNotFound) {
        toast.error(error.message || 'Комната не существует');
        navigate('/');
        return;
      }
      
      // Проверяем, требуется ли пароль
      if (error.needsPassword) {
        setPendingJoinParams({ overrideAudio, overrideVideo });
        setShowPasswordModal(true);
        setJoining(false);
        return;
      }
      
      toast.error(error.message || 'Не удалось подключиться');
      setJoining(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!roomPassword.trim()) {
      toast.error('Введите пароль');
      return;
    }
    setShowPasswordModal(false);
    
    // Если модальное окно было показано при входе в лобби - просто закрываем
    // Пользователь сам нажмёт кнопку "Присоединиться"
    if (roomRequiresPassword && !pendingJoinParams) {
      toast.success('Пароль сохранён');
      return;
    }
    
    // Повторяем попытку входа с паролем (если была неудачная попытка)
    if (pendingJoinParams) {
      await handleJoin(pendingJoinParams.overrideAudio, pendingJoinParams.overrideVideo);
      setPendingJoinParams(null);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleNameSubmit = () => {
    if (!modalName.trim()) {
      toast.error('Введите ваше имя');
      return;
    }
    setName(modalName.trim());
    localStorage.setItem('displayName', modalName.trim());
    setShowNameModal(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
      <div className="max-w-4xl w-full">
        <div className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gray-900 p-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">
                  Присоединение к комнате
                </h1>
                <p className="text-gray-400 text-sm">
                  ID комнаты: <span className="text-blue-400 font-mono">{roomId}</span>
                  {isHost && <span className="ml-2 text-yellow-400">👑 Организатор</span>}
                </p>
              </div>
              <Button variant="ghost" onClick={handleBack}>
                ← Назад
              </Button>
            </div>
          </div>

          {/* Preview */}
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Video Preview */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Превью камеры</h3>
                <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700">
                  {previewLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-2"></div>
                        <p className="text-gray-400 text-sm">Запуск камеры...</p>
                      </div>
                    </div>
                  ) : previewError ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-950/20">
                      <VideoOff className="w-16 h-16 text-red-600 mb-2" />
                      <p className="text-red-400 text-sm px-4 text-center">{previewError}</p>
                    </div>
                  ) : videoEnabled && previewStream ? (
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950">
                      <VideoOff className="w-16 h-16 text-gray-600 mb-2" />
                      <p className="text-gray-400 text-sm">Камера выключена</p>
                    </div>
                  )}
                </div>
                
                {/* Video Controls */}
                <div className="flex gap-2 mt-4">
                  <Button
                    variant={videoEnabled ? 'primary' : 'danger'}
                    onClick={() => setLocalVideoEnabled(!videoEnabled)}
                    className="flex-1"
                  >
                    {videoEnabled ? <Video className="w-5 h-5 mr-2" /> : <VideoOff className="w-5 h-5 mr-2" />}
                    {videoEnabled ? 'Камера вкл' : 'Камера выкл'}
                  </Button>
                  <Button
                    variant={audioEnabled ? 'primary' : 'danger'}
                    onClick={() => setLocalAudioEnabled(!audioEnabled)}
                    className="flex-1"
                  >
                    {audioEnabled ? <Mic className="w-5 h-5 mr-2" /> : <MicOff className="w-5 h-5 mr-2" />}
                    {audioEnabled ? 'Микрофон вкл' : 'Микрофон выкл'}
                  </Button>
                </div>
                
                {/* Background Blur Toggle */}
                <div className="mt-3">
                  <Button
                    variant={blurBackground ? 'primary' : 'secondary'}
                    onClick={() => setBlurBackground(!blurBackground)}
                    className="w-full"
                    disabled={!videoEnabled || previewLoading}
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    {blurBackground ? (blurProcessing ? 'Размытие фона...' : 'Размытие фона вкл') : 'Размытие фона'}
                  </Button>
                </div>
                
                {/* Hidden canvas for blur processing */}
                <canvas ref={canvasRef} className="hidden" />
              </div>

              {/* Settings */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Настройки
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Ваше имя
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Введите имя"
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Вы сможете изменить имя во время конференции
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Микрофон
                    </label>
                    <select 
                      value={audioDeviceId || ''}
                      onChange={(e) => setAudioDeviceId(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">По умолчанию</option>
                      {audioDevices.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Микрофон ${device.deviceId.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant={isMicTesting ? 'danger' : 'secondary'}
                      onClick={toggleMicTest}
                      className="w-full mt-2"
                      size="sm"
                    >
                      {isMicTesting ? (
                        <>
                          <Square className="w-4 h-4 mr-2" />
                          Остановить тест микрофона
                        </>
                      ) : (
                        <>
                          <Mic className="w-4 h-4 mr-2" />
                          Тест микрофона
                        </>
                      )}
                    </Button>
                    {isMicTesting && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <Volume2 className="w-4 h-4 text-gray-400" />
                          <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 transition-all duration-100"
                              style={{ width: `${micVolume}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-400 w-8">{Math.round(micVolume)}%</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Камера
                    </label>
                    <select 
                      value={videoDeviceId || ''}
                      onChange={(e) => setVideoDeviceId(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">По умолчанию</option>
                      {videoDevices.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Камера ${device.deviceId.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Динамики
                    </label>
                    <select 
                      value={audioOutputDeviceId || ''}
                      onChange={(e) => setAudioOutputDeviceId(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">По умолчанию</option>
                      {audioOutputDevices.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Динамик ${device.deviceId.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant={isSpeakerTesting ? 'danger' : 'secondary'}
                      onClick={playTestSound}
                      className="w-full mt-2"
                      size="sm"
                    >
                      {isSpeakerTesting ? (
                        <>
                          <Square className="w-4 h-4 mr-2" />
                          Остановить тест динамика
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Тест динамика
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-900 p-6 border-t border-gray-700">
            <div className="flex justify-end gap-4">
              <Button
                variant="secondary"
                onClick={() => handleJoin(false, videoEnabled)}
                disabled={joining || devicesLoading || !name.trim()}
              >
                <MicOff className="w-5 h-5 mr-2" />
                Войти без звука
              </Button>
              <Button
                onClick={() => handleJoin()}
                disabled={joining || devicesLoading || !name.trim()}
                size="lg"
              >
                {joining ? 'Подключение...' : 'Присоединиться'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal для ввода имени */}
      {showNameModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                Добро пожаловать!
              </h2>
              <p className="text-gray-400 text-sm">
                Введите ваше имя для участия в конференции
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Ваше имя
                </label>
                <input
                  type="text"
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                  placeholder="Например: Иван Петров"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={handleBack}
                  className="flex-1"
                >
                  Отмена
                </Button>
                <Button
                  onClick={handleNameSubmit}
                  className="flex-1"
                  disabled={!modalName.trim()}
                >
                  Продолжить
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal для ввода пароля */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                Комната защищена паролем
              </h2>
              <p className="text-gray-400 text-sm">
                Введите пароль для входа в комнату
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Пароль
                </label>
                <input
                  type="password"
                  value={roomPassword}
                  onChange={(e) => setRoomPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                  placeholder="Введите пароль"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPendingJoinParams(null);
                    // Если пароль требовался при входе - возвращаемся на главную
                    if (roomRequiresPassword) {
                      navigate('/');
                    }
                  }}
                  className="flex-1"
                >
                  Отмена
                </Button>
                <Button
                  onClick={handlePasswordSubmit}
                  className="flex-1"
                  disabled={!roomPassword.trim()}
                >
                  Войти
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lobby;
